import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  intl,
  HTTP_STATUS,
  DmsTable,
  DmsTableDeleteButton,
  DmsButtonGroup,
  DmsFlexContainer,
  fetchData,
  DmsButton,
  DmsModal,
  DmsTextField,
} from "dmsbasereact";
import Swal from "sweetalert2";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Chip,
  CircularProgress,
} from "@mui/material";

export const GroupsTab = () => {
  const table = useRef(null);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  const [newTrainingGroupName, setNewTrainingGroupName] = useState("");
  const [newTrainingGroupDescription, setNewTrainingGroupDescription] = useState("");
  const [selectedTrainings, setSelectedTrainings] = useState([]);
  const [selectedInstitutionalGroups, setSelectedInstitutionalGroups] = useState([]);

  const [availableTrainings, setAvailableTrainings] = useState([]);
  const [availableInstitutionalGroups, setAvailableInstitutionalGroups] = useState([]);
  const [loadingTrainings, setLoadingTrainings] = useState(false);
  const [loadingInstitutionalGroups, setLoadingInstitutionalGroups] = useState(false);

  const loadAvailableTrainings = useCallback(async () => {
    setLoadingTrainings(true);
    try {
      const response = await fetchData("/training/all", "GET", true);
      const data = response?.data?.data || response?.data || [];
      setAvailableTrainings(Array.isArray(data) ? data : []);
    } catch (error) {
      setAvailableTrainings([]);
    } finally {
      setLoadingTrainings(false);
    }
  }, []);

  const loadAvailableInstitutionalGroups = useCallback(async () => {
    setLoadingInstitutionalGroups(true);
    try {
      const response = await fetchData("/institutional_group/all", "GET", true);
      const data = response?.data?.data || response?.data || [];
      setAvailableInstitutionalGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      setAvailableInstitutionalGroups([]);
    } finally {
      setLoadingInstitutionalGroups(false);
    }
  }, []);

  useEffect(() => {
    if (isCreateModalOpen || isEditModalOpen) {
      loadAvailableTrainings();
      loadAvailableInstitutionalGroups();
    }
  }, [isCreateModalOpen, isEditModalOpen, loadAvailableTrainings, loadAvailableInstitutionalGroups]);

  const handleOpenCreateModal = () => setCreateModalOpen(true);
  const handleCloseCreateModal = () => {
      setCreateModalOpen(false);
      setNewTrainingGroupName("");
      setNewTrainingGroupDescription("");
      setSelectedTrainings([]);
      setSelectedInstitutionalGroups([]);
  };

  const handleOpenEditModal = (row) => {
    const trainingPks = Array.isArray(row.training_associations)
      ? row.training_associations.map(a => a.training_pk).filter(pk => typeof pk === 'number')
      : [];
    const igPks = Array.isArray(row.institutional_group_associations)
      ? row.institutional_group_associations.map(a => a.institutional_group_pk).filter(pk => typeof pk === 'number')
      : [];

    setEditingGroup(row);
    setNewTrainingGroupName(row.name || "");
    setNewTrainingGroupDescription(row.description || "");
    setSelectedTrainings(trainingPks);
    setSelectedInstitutionalGroups(igPks);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingGroup(null);
    setNewTrainingGroupName("");
    setNewTrainingGroupDescription("");
    setSelectedTrainings([]);
    setSelectedInstitutionalGroups([]);
  };

  const handleCreateSave = async () => {
    if (!newTrainingGroupName) {
      Swal.fire({ icon: "warning", title: intl.get("MissingInformation").d("Missing Information"), text: intl.get("PleaseEnterName").d("Please enter a name.") });
      return;
    }

    try {
      const payload = {
          name: newTrainingGroupName,
          description: newTrainingGroupDescription,
          trainings: selectedTrainings,
          institutional_groups: selectedInstitutionalGroups,
      }
      const response = await fetchData('/training_group', 'POST', true, payload);
      if (response.status !== HTTP_STATUS.CREATED_201) {
        throw new Error(response?.data?.detail || "Failed to save training group.");
      }

      Swal.fire({ icon: "success", title: intl.get("Success").d("Success"), text: intl.get("TrainingGroupCreated").d("Training group created successfully.") });
      table.current.updateTable();
      handleCloseCreateModal();
    } catch (error) {
      Swal.fire({ icon: "error", title: intl.get("Error").d("Error"), text: error.message });
    }
  };

  const handleEditSave = async () => {
    if (!newTrainingGroupName) {
      Swal.fire({ icon: "warning", title: intl.get("MissingInformation").d("Missing Information"), text: intl.get("PleaseEnterName").d("Please enter a name.") });
      return;
    }

    try {
      const payload = {
          name: newTrainingGroupName,
          description: newTrainingGroupDescription,
          trainings: selectedTrainings,
          institutional_groups: selectedInstitutionalGroups,
      }
      const response = await fetchData(`/training_group/${editingGroup.pk}`, 'PUT', true, payload);
      if (response.status !== HTTP_STATUS.OK_200) {
        throw new Error(response?.data?.detail || "Failed to update training group.");
      }

      Swal.fire({ icon: "success", title: intl.get("Success").d("Success"), text: intl.get("TrainingGroupUpdated").d("Training group updated successfully.") });
      table.current.updateTable();
      handleCloseEditModal();
    } catch (error) {
      Swal.fire({ icon: "error", title: intl.get("Error").d("Error"), text: error.message });
    }
  };

  const header = [
    { label: intl.get("Name").d("Name"), column_name: "name", filter: true },
    { label: intl.get("Description").d("Description"), column_name: "description", filter: true, control: 'textarea' },
    {
        label: intl.get("Trainings").d("Trainings"),
        column_name: "training_associations",
        control: 'select',
        url: "/training/all",
        sortable: false,
        properties: {
            column_name: "name",
            text_template: '{name}',
            multiple: true,
        },
        function: (value) => {
            if (!Array.isArray(value)) {
                return '-';
            }
            return value.map(v => (v && v.training && v.training.name) ? v.training.name : '').join(', ');
        },
    },
    {
        label: intl.get("InstitutionalGroups").d("Institutional Groups"),
        column_name: "institutional_group_associations",
        control: 'select',
        url: "/institutional_group/all",
        sortable: false,
        properties: {
            column_name: "name",
            text_template: '{name}',
            multiple: true,
        },
        function: (value) => {
            if (!Array.isArray(value)) {
                return '-';
            }
            return value.map(v => (v && v.institutional_group && v.institutional_group.name) ? v.institutional_group.name : '').join(', ');
        },
    },
    {
      label: intl.get("Actions").d("Actions"),
      column_name: "actions",
      width: "120px",
      sortable: false,
      element: (row) => (
        <DmsButtonGroup>
          <DmsButton
            variant="primary"
            icon={<EditIcon />}
            onClick={() => handleOpenEditModal(row)}
            title={intl.get("Tooltip.Action.Edit").d("Edit")}
          />
          <DmsTableDeleteButton
            table={table}
            title={intl.get("DeleteTrainingGroup").d("Delete Training Group")}
            text={intl.get("TrainingGroupPage.DeleteButtonMessage", { name: row.name }).d(`The training group {name} will be deleted. Are you sure?`)}
            url="/training_group/"
            row_pk={row.pk}
          />
        </DmsButtonGroup>
      ),
    },
  ];

  const toolbar = (
    <DmsFlexContainer>
      <DmsFlexContainer.Left>
        <DmsButton variant="primary" icon={<AddIcon />} onClick={handleOpenCreateModal} text={intl.get("CreateTrainingGroup").d("Create Training Group")} />
      </DmsFlexContainer.Left>
    </DmsFlexContainer>
  );

  return (
    <>
      <DmsTable header={header} managed={true} needs_authentication={true} ref={table} url={'/training_group/table'} toolbar={toolbar} />

      <DmsModal isVisible={isCreateModalOpen} onClose={handleCloseCreateModal}>
        <DmsModal.Header title={intl.get("CreateTrainingGroup").d("Create Training Group")} onClose={handleCloseCreateModal} />
        <DmsModal.Body>
            <Box sx={{mt: 2}}>
                <DmsTextField
                    name="name"
                    label={intl.get("Name").d("Name")}
                    value={newTrainingGroupName}
                    onChange={(e) => setNewTrainingGroupName(e.target.value)}
                    fullWidth
                    required
                />
            </Box>
            <Box sx={{mt: 2}}>
                <DmsTextField
                    name="description"
                    label={intl.get("Description").d("Description")}
                    value={newTrainingGroupDescription}
                    onChange={(e) => setNewTrainingGroupDescription(e.target.value)}
                    fullWidth
                    multiline
                    rows={4}
                />
            </Box>
            <Box sx={{mt: 2}}>
                <FormControl fullWidth disabled={loadingTrainings}>
                  <InputLabel>{intl.get("Trainings").d("Trainings")}</InputLabel>
                  <Select
                    multiple
                    value={selectedTrainings}
                    label={intl.get("Trainings").d("Trainings")}
                    onChange={(e) => setSelectedTrainings(e.target.value)}
                    input={<OutlinedInput label={intl.get("Trainings").d("Trainings")} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((pk) => {
                          const training = availableTrainings.find(t => t.pk === pk);
                          return <Chip key={pk} label={training?.name || pk} size="small" />;
                        })}
                      </Box>
                    )}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                  >
                    {loadingTrainings ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        {intl.get("Loading").d("Loading...")}
                      </MenuItem>
                    ) : (
                      availableTrainings.map((training) => (
                        <MenuItem key={training.pk} value={training.pk}>
                          <Checkbox checked={selectedTrainings.indexOf(training.pk) > -1} />
                          <ListItemText primary={training.name} />
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
            </Box>
            <Box sx={{mt: 2}}>
                <FormControl fullWidth disabled={loadingInstitutionalGroups}>
                  <InputLabel>{intl.get("InstitutionalGroups").d("Institutional Groups")}</InputLabel>
                  <Select
                    multiple
                    value={selectedInstitutionalGroups}
                    label={intl.get("InstitutionalGroups").d("Institutional Groups")}
                    onChange={(e) => setSelectedInstitutionalGroups(e.target.value)}
                    input={<OutlinedInput label={intl.get("InstitutionalGroups").d("Institutional Groups")} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((pk) => {
                          const ig = availableInstitutionalGroups.find(g => g.pk === pk);
                          return <Chip key={pk} label={ig?.name || pk} size="small" />;
                        })}
                      </Box>
                    )}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                  >
                    {loadingInstitutionalGroups ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        {intl.get("Loading").d("Loading...")}
                      </MenuItem>
                    ) : (
                      availableInstitutionalGroups.map((ig) => (
                        <MenuItem key={ig.pk} value={ig.pk}>
                          <Checkbox checked={selectedInstitutionalGroups.indexOf(ig.pk) > -1} />
                          <ListItemText primary={ig.name} />
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
            </Box>
        </DmsModal.Body>
        <DmsModal.Footer>
            <DmsButton variant="secondary" text={intl.get("Cancel").d("Cancel")} onClick={handleCloseCreateModal} />
            <DmsButton variant="primary" onClick={handleCreateSave} text={intl.get("Save").d("Save")} />
        </DmsModal.Footer>
      </DmsModal>

      {isEditModalOpen && (
        <DmsModal isVisible={isEditModalOpen} onClose={handleCloseEditModal}>
          <DmsModal.Header title={intl.get("EditTrainingGroup").d("Edit Training Group")} onClose={handleCloseEditModal} />
          <DmsModal.Body>
              <Box sx={{mt: 2}}>
                  <DmsTextField
                      name="name"
                      label={intl.get("Name").d("Name")}
                      value={newTrainingGroupName}
                      onChange={(e) => setNewTrainingGroupName(e.target.value)}
                      fullWidth
                      required
                  />
              </Box>
              <Box sx={{mt: 2}}>
                  <DmsTextField
                      name="description"
                      label={intl.get("Description").d("Description")}
                      value={newTrainingGroupDescription}
                      onChange={(e) => setNewTrainingGroupDescription(e.target.value)}
                      fullWidth
                      multiline
                      rows={4}
                  />
              </Box>
              <Box sx={{mt: 2}}>
                <FormControl fullWidth disabled={loadingTrainings}>
                  <InputLabel>{intl.get("Trainings").d("Trainings")}</InputLabel>
                  <Select
                    multiple
                    value={selectedTrainings}
                    label={intl.get("Trainings").d("Trainings")}
                    onChange={(e) => setSelectedTrainings(e.target.value)}
                    input={<OutlinedInput label={intl.get("Trainings").d("Trainings")} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((pk) => {
                          const training = availableTrainings.find(t => t.pk === pk);
                          return <Chip key={pk} label={training?.name || pk} size="small" />;
                        })}
                      </Box>
                    )}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                  >
                    {loadingTrainings ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        {intl.get("Loading").d("Loading...")}
                      </MenuItem>
                    ) : (
                      availableTrainings.map((training) => (
                        <MenuItem key={training.pk} value={training.pk}>
                          <Checkbox checked={selectedTrainings.indexOf(training.pk) > -1} />
                          <ListItemText primary={training.name} />
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{mt: 2}}>
                <FormControl fullWidth disabled={loadingInstitutionalGroups}>
                  <InputLabel>{intl.get("InstitutionalGroups").d("Institutional Groups")}</InputLabel>
                  <Select
                    multiple
                    value={selectedInstitutionalGroups}
                    label={intl.get("InstitutionalGroups").d("Institutional Groups")}
                    onChange={(e) => setSelectedInstitutionalGroups(e.target.value)}
                    input={<OutlinedInput label={intl.get("InstitutionalGroups").d("Institutional Groups")} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((pk) => {
                          const ig = availableInstitutionalGroups.find(g => g.pk === pk);
                          return <Chip key={pk} label={ig?.name || pk} size="small" />;
                        })}
                      </Box>
                    )}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                  >
                    {loadingInstitutionalGroups ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        {intl.get("Loading").d("Loading...")}
                      </MenuItem>
                    ) : (
                      availableInstitutionalGroups.map((ig) => (
                        <MenuItem key={ig.pk} value={ig.pk}>
                          <Checkbox checked={selectedInstitutionalGroups.indexOf(ig.pk) > -1} />
                          <ListItemText primary={ig.name} />
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>
          </DmsModal.Body>
          <DmsModal.Footer>
              <DmsButton variant="secondary" text={intl.get("Cancel").d("Cancel")} onClick={handleCloseEditModal} />
              <DmsButton variant="primary" onClick={handleEditSave} text={intl.get("Update").d("Update")} />
          </DmsModal.Footer>
        </DmsModal>
      )}
    </>
  );
};
